import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

/**
 * DecisionDialog — Pattern 3: neutral in-app decision (NOT a deletion).
 * Symmetric equal-size buttons: Cancel = white/neutral outline (border #C2C9D1),
 * primary action = GREEN (#077A39). Replaces native window.confirm.
 *
 * Props:
 *   open        — boolean
 *   onClose     — () => void
 *   onConfirm   — () => void
 *   title       — dialog title
 *   children    — optional body content (JSX)
 *   actionLabel — blue button text (e.g. "Untie", "Dismiss")
 *   busy        — optional loading state
 *   busyLabel   — optional custom busy text (default: "{actionLabel}…")
 *   testid      — optional data-testid prefix (default "decision-dialog")
 */
export function DecisionDialog({ open, onClose, onConfirm, title, children, actionLabel = "Confirm", busy = false, busyLabel, testid = "decision-dialog" }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter className="mt-2 gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 rounded-md border border-[#C2C9D1] bg-white px-4 py-2 text-sm font-bold text-[#374151] hover:bg-[#F4F7FB] disabled:opacity-40"
            data-testid={`${testid}-cancel`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 rounded-md bg-[#077A39] px-4 py-2 text-sm font-bold text-white hover:bg-[#065f2c] disabled:opacity-40"
            data-testid={`${testid}-confirm`}
          >
            {busy ? (busyLabel || `${actionLabel}\u2026`) : actionLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
